import { useEffect, useRef, useState, lazy, Suspense } from "react";
import { Switch, Route, Router as WouterRouter, Link, useLocation } from "wouter";
import { QueryClient, QueryClientProvider, MutationCache, useQuery } from "@tanstack/react-query";
import { AuthProvider } from "@/contexts/AuthContext";
import { useAuth } from "@/hooks/useAuth";
import { setTenantIdGetter, setAuthTokenGetter, customFetch } from "@workspace/api-client-react";
import { ThemeDbSync } from "@/providers/ThemeProvider";

import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import NotFound from "@/pages/not-found";
import { ClerkQueryClientCacheInvalidator } from "@/components/auth/clerk-cache-invalidator";

import { ShieldAlert, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import AppShell from "@/components/shell/AppShell";
import VendorShell from "@/components/shell/VendorShell";
import { modulesForPath } from "@/lib/routeModules";
import { getDashboardRouteForRole } from "@/lib/dashboardRoutes";

// ── Code Splitting: Lazy-loaded page components ─────────────────────────────
// Marketing pages stay eager (small, needed for initial landing page paint).
// All authenticated ERP module pages are lazy-loaded to reduce initial bundle.
const OnboardingPage = lazy(() => import("@/pages/onboarding"));
const EmployeeProfilePage = lazy(() => import("@/pages/employee-profile"));
const AccountSettingsPage = lazy(() => import("@/pages/account-settings"));
const Launchpad = lazy(() => import("@/pages/launchpad"));
const Dashboard = lazy(() => import("@/pages/dashboard"));
const OrganizationPage = lazy(() => import("@/pages/organization"));
const UsersPage = lazy(() => import("@/pages/users"));
const AuditLogPage = lazy(() => import("@/pages/audit-log"));
const HRPage = lazy(() => import("@/pages/hr"));
const EmployeePage = lazy(() => import("@/pages/employee"));
const FinancePage = lazy(() => import("@/pages/finance"));
const VendorPortalPage = lazy(() => import("@/pages/vendor-portal"));
const CatalogPage = lazy(() => import("@/pages/catalog"));
const PayrollPage = lazy(() => import("@/pages/payroll"));
const TallyPage = lazy(() => import("@/pages/tally"));
const InventoryPage = lazy(() => import("@/pages/inventory"));
const CrmPage = lazy(() => import("@/pages/crm"));
const OperationsPage = lazy(() => import("@/pages/operations"));
const ProcurementPage = lazy(() => import("@/pages/procurement"));
const AdminPage = lazy(() => import("@/pages/admin"));
const PlatformAdminHub = lazy(() => import("@/pages/platform-admin/PlatformAdminHub"));
const ReportsPage = lazy(() => import("@/pages/reports"));
const RecruitmentPage = lazy(() => import("@/pages/recruitment"));
const QualityPage = lazy(() => import("@/pages/quality"));
const ManufacturingPage = lazy(() => import("@/pages/manufacturing"));
const ApprovalsPage = lazy(() => import("@/pages/approvals"));
const PerformancePage = lazy(() => import("@/pages/performance"));
const ServicePage = lazy(() => import("@/pages/service"));
const IntegrationsPage = lazy(() => import("@/pages/integrations"));
const DmsPage = lazy(() => import("@/pages/dms"));
const LDPage = lazy(() => import("@/pages/ld"));
const TravelPage = lazy(() => import("@/pages/travel"));
const ForgotPasswordPage = lazy(() => import("@/pages/forgot-password"));
const ResetPasswordPage = lazy(() => import("@/pages/reset-password"));
const SetPasswordPage = lazy(() => import("@/pages/set-password"));
const SignInPage = lazy(() => import("@/pages/auth/SignInPage"));
const SignUpPage = lazy(() => import("@/pages/auth/SignUpPage"));
const DevToolsPage = lazy(() => import("@/pages/dev-tools"));
const HelpdeskPage = lazy(() => import("@/pages/helpdesk"));
const PublicFormViewer = lazy(() => import("@/pages/PublicFormViewer"));
const SuperAdminPackagesPage = lazy(() => import("@/pages/superadmin/SuperAdminPackagesPage").then(m => ({ default: m.SuperAdminPackagesPage })));
const PublicPricingPage = lazy(() => import("@/pages/public/PublicPricingPage").then(m => ({ default: m.PublicPricingPage })));
import PackageDetails from "@/marketing/pages/PackageDetails";

// Marketing pages — eager (small, critical for first paint)
import { Logo } from "@/marketing/components/Logo";
import { AuthBrandingPanel } from "@/components/auth/AuthBrandingPanel";
import Home from "@/marketing/pages/Home";
import About from "@/marketing/pages/About";
import Features from "@/marketing/pages/Features";
import Pricing from "@/marketing/pages/Pricing";
import DemoAccess from "@/marketing/pages/DemoAccess";
import Contact from "@/marketing/pages/Contact";
import Privacy from "@/marketing/pages/Privacy";
import Terms from "@/marketing/pages/Terms";

// ── Route-level Suspense fallback ────────────────────────────────────────────
function PageLoading() {
  return (
    <div className="flex items-center justify-center h-full min-h-[40vh]">
      <div className="flex flex-col items-center gap-3 text-slate-400">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-slate-300 border-t-blue-600" />
        <span className="text-sm">Loading module…</span>
      </div>
    </div>
  );
}

// Wire multi-company tenant selection: every API call will carry X-Tenant-Id
// matching whatever the user last selected in the company switcher.
setTenantIdGetter(() => localStorage.getItem("selectedTenantId"));

// ClerkTokenSync — runs inside <ClerkProvider> so useAuth() is available.
// Registers a Bearer-token getter so customFetch attaches an
// Authorization: Bearer <token> header on every API request.
// This is necessary because the ERP frontend (port 5173) and API server
// (port 8080) are different origins in Replit dev — Clerk session cookies
// set by the frontend are never forwarded to the API server automatically.
//
// Uses refs to avoid teardown/re-registration flicker: the getter is
// registered ONCE on mount via a stable wrapper that always reads from
// up-to-date refs, so there is no window where the getter is null between
// Clerk re-renders (which would briefly cause 401 spikes).
function ClerkTokenSync() {
  const { getToken, isSignedIn } = useAuth();
  const getTokenRef = useRef(getToken);
  const isSignedInRef = useRef(isSignedIn);

  // Keep refs in sync every render (no effect needed — synchronous update)
  getTokenRef.current = getToken;
  isSignedInRef.current = isSignedIn;

  // Register the stable getter ONCE on mount; clear only on unmount.
  useEffect(() => {
    setAuthTokenGetter(() => {
      if (!isSignedInRef.current) return null;
      return getTokenRef.current();
    });
    return () => { setAuthTokenGetter(null); };
  }, []);

  return null;
}

// Automatically refresh on-screen data after every successful mutation, so the
// acting user sees their create/update/delete instantly without a manual refresh.
//
// Orval's generated query keys are API-path based (e.g. ["/api/hr/employees"]),
// but many pages historically invalidated with short, mismatched keys (e.g.
// ["listEmployees"]). Those invalidations silently no-op'd, so the UI only
// updated on tab-switch (refetchOnWindowFocus) or a full page refresh. A single
// global mutation handler that invalidates all queries guarantees fresh data
// regardless of per-call key correctness. invalidateQueries only refetches
// ACTIVE (mounted) queries; inactive ones are merely marked stale. We do not
// return the promise so dialogs/toasts close immediately while data refetches
// in the background.
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,       // 5 min — avoid refetching on every mount
      gcTime: 10 * 60 * 1000,          // 10 min — keep cached data longer
      refetchOnWindowFocus: false,      // prevent surprise refetches
      retry: 2,                         // retry failed queries twice
      networkMode: 'online',            // don't queue offline requests
    },
    mutations: {
      retry: 1,
    },
  },
  mutationCache: new MutationCache({
    onSuccess: () => {
      queryClient.invalidateQueries();
    },
  }),
});

// Use VITE_CLERK_PUBLISHABLE_KEY directly with production fallback
const clerkPubKey =
  import.meta.env.VITE_CLERK_PUBLISHABLE_KEY ||
  "pk_test_cHJldHR5LWNhdC00NC5jbGVyay5hY2NvdW50cy5kZXYk";

const clerkProxyUrl = import.meta.env.VITE_CLERK_PROXY_URL;
const basePath = import.meta.env.BASE_URL.replace(/\/$/, "");

/** True when an API error is the backend's "this is a vendor account" signal. */
function isVendorAccountError(error: unknown): boolean {
  const e = error as { status?: number; data?: { code?: string } } | null;
  return !!e && e.status === 403 && e.data?.code === "VENDOR_ACCOUNT";
}

// ─── Tenant lifecycle status screens ─────────────────────────────────────────

function TenantStatusScreen({ status, reason }: { status: string; reason?: string | null }) {
  const { signOut } = useAuth();

  const config = {
    PENDING_APPROVAL: {
      icon: (
        <div className="flex h-20 w-20 items-center justify-center rounded-full bg-amber-50 border-2 border-amber-200">
          <svg className="h-10 w-10 text-amber-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6l4 2m6-2a10 10 0 11-20 0 10 10 0 0120 0z" />
          </svg>
        </div>
      ),
      title: "Awaiting Approval",
      subtitle: "Your organisation registration is under review",
      body: "Our team is reviewing your application. You'll receive an in-app notification and email as soon as a decision is made — typically within 1 business day.",
      badge: { text: "Pending Review", cls: "bg-amber-100 text-amber-700 border-amber-200" },
    },
    REJECTED: {
      icon: (
        <div className="flex h-20 w-20 items-center justify-center rounded-full bg-red-50 border-2 border-red-200">
          <svg className="h-10 w-10 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
      ),
      title: "Application Not Approved",
      subtitle: "Your organisation application was not accepted",
      body: reason
        ? `Reason: ${reason}`
        : "Your registration could not be approved at this time. Please contact our support team for assistance.",
      badge: { text: "Rejected", cls: "bg-red-100 text-red-700 border-red-200" },
    },
    SUSPENDED: {
      icon: (
        <div className="flex h-20 w-20 items-center justify-center rounded-full bg-orange-50 border-2 border-orange-200">
          <svg className="h-10 w-10 text-orange-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
          </svg>
        </div>
      ),
      title: "Account Suspended",
      subtitle: "Your organisation's access has been temporarily suspended",
      body: reason
        ? `Suspension reason: ${reason}`
        : "Access has been suspended pending review. Please contact support to resolve this and restore access.",
      badge: { text: "Suspended", cls: "bg-orange-100 text-orange-700 border-orange-200" },
    },
    DEACTIVATED: {
      icon: (
        <div className="flex h-20 w-20 items-center justify-center rounded-full bg-slate-100 border-2 border-slate-200">
          <svg className="h-10 w-10 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88" />
          </svg>
        </div>
      ),
      title: "Account Deactivated",
      subtitle: "This organisation account is no longer active",
      body: "This account has been permanently deactivated. Please contact our support team if you believe this is an error or if you need to start a new registration.",
      badge: { text: "Deactivated", cls: "bg-slate-100 text-slate-500 border-slate-200" },
    },
  }[status] ?? null;

  if (!config) return null;

  return (
    <div className="min-h-[100dvh] flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-950 p-4">
      <div className="w-full max-w-md">
        {/* Card */}
        <div className="rounded-2xl border border-border bg-white shadow-lg p-8 space-y-6">
          {/* Logo */}
          <div className="flex justify-center">
            <Link href="/" className="inline-flex hover:opacity-90 transition-opacity" aria-label="Alea ERP home">
              <Logo className="h-8" />
            </Link>
          </div>

          {/* Icon + Badge */}
          <div className="flex flex-col items-center gap-3">
            {config.icon}
            <span className={`inline-flex items-center rounded-full border px-3 py-1 text-xs font-semibold ${config.badge.cls}`}>
              {config.badge.text}
            </span>
          </div>

          {/* Copy */}
          <div className="text-center space-y-2">
            <h1 className="text-xl font-bold text-[#0a2d4a] dark:text-white">{config.title}</h1>
            <p className="text-sm font-medium text-slate-600">{config.subtitle}</p>
            <p className="text-sm text-muted-foreground leading-relaxed">{config.body}</p>
          </div>

          {/* Support contact */}
          <div className="rounded-xl bg-slate-50 border border-slate-100 p-4 text-center text-xs text-muted-foreground">
            Questions? Email us at{" "}
            <a href="mailto:support@aleaerp.com" className="text-blue-600 underline font-medium">
              support@aleaerp.com
            </a>
          </div>

          {/* Sign out */}
          <button
            onClick={() => signOut()}
            className="w-full rounded-lg border border-border bg-white py-2 text-sm font-medium text-slate-600 hover:bg-slate-50 transition-colors"
          >
            Sign out
          </button>
        </div>

        <p className="mt-4 text-center text-xs text-muted-foreground">
          Alea ERP · Enterprise Resource Planning
        </p>
      </div>
    </div>
  );
}

function AuthGuard({ children, skipOnboardingCheck }: { children: React.ReactNode; skipOnboardingCheck?: boolean }) {
  const { isLoaded, userId } = useAuth();
  const { data: me, status: meStatus, error: meError } = useQuery<Record<string, unknown>>({
    queryKey: ["me"],
    queryFn: () => customFetch<Record<string, unknown>>("/api/me"),
    enabled: isLoaded && !!userId,
    staleTime: 30_000,
    retry: 1,
  });
  const [location, setLocation] = useLocation();
  const isVendor = isVendorAccountError(meError);

  const tenantStatus = me?.tenantStatus as string | undefined;
  const tenantIsNonActive = !!tenantStatus && tenantStatus !== "ACTIVE";

  useEffect(() => {
    if (isLoaded && !userId) {
      setLocation("/sign-in");
    } else if (isVendor) {
      // A vendor logged into the employee app — send them to their portal.
      setLocation("/vendor-portal");
    } else if (isLoaded && userId && me && me.isSuperAdmin === true && (location === "/dashboard" || location === "/dashboard/")) {
      // Platform super-admins directly go to the Platform Admin panel.
      setLocation("/admin/platform");
    } else if (
      !skipOnboardingCheck &&
      isLoaded &&
      userId &&
      me &&
      me.isSuperAdmin !== true &&
      me.isOnboarded === false &&
      (!tenantStatus || tenantStatus === "ACTIVE" || tenantStatus === "PENDING_APPROVAL")
    ) {
      // Allow PENDING tenants to complete onboarding so their info is ready for review.
      // Platform super-admins never go through company onboarding.
      setLocation("/onboarding");
    }
  }, [isLoaded, userId, me, isVendor, skipOnboardingCheck, tenantStatus, location, setLocation]);

  if (isVendor) {
    return (
      <div className="min-h-[100dvh] flex items-center justify-center bg-[#f4f6f8]">
        <div className="flex flex-col items-center gap-3 text-slate-400">
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-slate-300 border-t-blue-600" />
          <span className="text-sm">Redirecting to vendor portal…</span>
        </div>
      </div>
    );
  }

  const spinner = (
    <div className="min-h-[100dvh] flex items-center justify-center bg-[#f4f6f8]">
      <div className="flex flex-col items-center gap-3 text-slate-400">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-slate-300 border-t-blue-600" />
        <span className="text-sm">Loading…</span>
      </div>
    </div>
  );

  if (!isLoaded) return spinner;
  if (!userId) return spinner;
  if (!skipOnboardingCheck && meStatus === "pending") return spinner;

  if (meStatus === "error") {
    return (
      <div className="min-h-[100dvh] flex items-center justify-center bg-[#f4f6f8] px-4">
        <div className="max-w-md w-full rounded-2xl bg-white p-6 shadow-xl border border-border text-center space-y-4">
          <div className="w-12 h-12 rounded-full bg-red-100 text-red-600 flex items-center justify-center mx-auto text-xl font-bold">
            !
          </div>
          <h2 className="text-lg font-bold text-slate-800">Unable to load workspace profile</h2>
          <p className="text-sm text-slate-500">
            {(meError as any)?.message || "A server error occurred while retrieving your workspace details."}
          </p>
          <div className="flex items-center justify-center gap-3 pt-2">
            <Button variant="outline" size="sm" onClick={() => window.location.reload()}>
              Retry
            </Button>
            <Button size="sm" onClick={() => { localStorage.clear(); window.location.href = "/sign-in"; }}>
              Sign in Again
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Show tenant lifecycle status screen if access is blocked
  if (me && tenantIsNonActive) {
    // PENDING tenants who haven't completed onboarding go to /onboarding first.
    // If skipOnboardingCheck is true we are already ON /onboarding — render
    // children so the page displays instead of looping back to a spinner.
    if (tenantStatus === "PENDING_APPROVAL" && me.isOnboarded === false) {
      if (skipOnboardingCheck) return <>{children}</>;
      return spinner; // waiting for redirect in useEffect
    }
    return (
      <TenantStatusScreen
        status={tenantStatus!}
        reason={(me.tenantRejectionReason ?? me.tenantSuspensionReason) as string | null}
      />
    );
  }

  if (!skipOnboardingCheck && me && me.isSuperAdmin !== true && me.isOnboarded === false) return spinner;
  return <>{children}</>;
}

/**
 * Strict Guard for Platform Super Admin Panel (/admin/platform/*).
 * Completely blocks non-super-admins and tenant admins from viewing or interacting with platform admin pages.
 */
function SuperAdminGuard({ children }: { children: React.ReactNode }) {
  const { isLoaded, userId } = useAuth();
  const { data: me, isLoading } = useQuery<Record<string, unknown>>({
    queryKey: ["me"],
    queryFn: () => customFetch<Record<string, unknown>>("/api/me"),
    enabled: isLoaded && !!userId,
    staleTime: 30_000,
  });
  const [, setLocation] = useLocation();

  useEffect(() => {
    if (isLoaded && !userId) setLocation("/sign-in");
  }, [isLoaded, userId, setLocation]);

  if (!isLoaded || !userId || isLoading) {
    return (
      <div className="min-h-[100dvh] flex items-center justify-center bg-[#0a2d4a]">
        <div className="flex flex-col items-center gap-3 text-slate-300">
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-blue-400 border-t-white" />
          <span className="text-sm font-medium">Verifying Platform Super-Admin Credentials…</span>
        </div>
      </div>
    );
  }

  if (!me || me.isSuperAdmin !== true) {
    return (
      <div className="min-h-[100dvh] flex items-center justify-center bg-slate-950 p-6 text-center">
        <div className="max-w-md space-y-4 rounded-2xl border border-red-500/20 bg-slate-900 p-8 shadow-2xl">
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-red-500/10 text-red-400 border border-red-500/20">
            <ShieldAlert className="h-7 w-7" />
          </div>
          <h2 className="text-xl font-extrabold text-white">403 Forbidden: Platform Admin Only</h2>
          <p className="text-sm text-slate-400 leading-relaxed">
            Access to the Platform Admin Control Center is strictly restricted to Super Administrators. Your account does not have permission to view this panel.
          </p>
          <div className="pt-2">
            <Button onClick={() => setLocation("/dashboard")} className="w-full bg-blue-600 hover:bg-blue-500 text-white font-semibold">
              Return to My Dashboard
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}

/**
 * Module Access Guard for Tenant Operational Routes.
 * - Restricts Platform Super-Admins to Platform Admin surface.
 * - Enforces role permissions using the shared path → modules map
 *   (`modulesForPath`) so guards stay aligned with the sidebar + APIs.
 */
function RequireModuleAccess({
  moduleName,
  children,
}: {
  moduleName: string;
  children: React.ReactNode;
}) {
  const { data: me, isLoading } = useQuery<Record<string, unknown>>({
    queryKey: ["me"],
    queryFn: () => customFetch<Record<string, unknown>>("/api/me"),
    staleTime: 30_000,
  });
  const [location, setLocation] = useLocation();

  useEffect(() => {
    if (me?.isSuperAdmin === true) {
      setLocation("/admin/platform");
    }
  }, [me, setLocation]);

  if (isLoading || !me) return null;

  // 1. Platform Super-Admins operate in Platform Admin surface — auto-redirect
  if (me.isSuperAdmin === true) {
    return (
      <div className="min-h-[100dvh] flex items-center justify-center bg-[#0a2d4a]">
        <div className="flex flex-col items-center gap-3 text-slate-300">
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-blue-400 border-t-white" />
          <span className="text-sm font-medium">Redirecting to Platform Admin Panel…</span>
        </div>
      </div>
    );
  }

  // 2. Tenant admins / full-access roles unlock every operational panel
  const roleName = String(me.roleName || "");
  const permissions = Array.isArray(me.permissions)
    ? (me.permissions as unknown[]).filter((p): p is string => typeof p === "string")
    : [];
  const isOrgAdmin =
    roleName === "Org Admin" ||
    roleName === "Admin" ||
    roleName === "Super Admin" ||
    roleName === "Full Access (Matrix)" ||
    permissions.some((p) => p.startsWith("admin:"));

  if (isOrgAdmin) {
    return <>{children}</>;
  }

  // 3. Shared path → RBAC modules (null = open self-service / always-on surfaces)
  const moduleKeys = modulesForPath(location);
  if (moduleKeys === null) {
    return <>{children}</>;
  }

  // Operations → Tickets is a self-service surface for assignees who lack
  // operations:view. Backend enforces assignee-only ticket access.
  const cleanPath = location.split("?")[0].split("#")[0].replace(/\/$/, "");
  const urlTab =
    typeof window !== "undefined"
      ? new URLSearchParams(window.location.search).get("tab")
      : null;
  if (cleanPath === "/operations" && urlTab === "tickets") {
    return <>{children}</>;
  }
  // Self-service personal tabs on payroll (e.g. My Payslips) are open to all authenticated employees
  if (
    cleanPath.includes("payroll") &&
    (!urlTab || urlTab.startsWith("my-") || cleanPath.endsWith("/salary-slips") || cleanPath.includes("/salary-slips/") || cleanPath.endsWith("/my-payslips"))
  ) {
    return <>{children}</>;
  }

  const hasModulePermission = moduleKeys.some((key) =>
    permissions.some((p) => typeof p === "string" && p.startsWith(`${key}:`)),
  );

  if (!hasModulePermission) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center p-6 text-center">
        <div className="max-w-md space-y-4 rounded-2xl border border-rose-500/20 bg-card p-8 shadow-xl">
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-rose-500/10 text-rose-500 border border-rose-500/20">
            <Lock className="h-7 w-7" />
          </div>
          <h2 className="text-xl font-extrabold text-foreground">Access Restricted</h2>
          <p className="text-sm text-muted-foreground leading-relaxed">
            You do not have role permissions to access the <span className="font-semibold text-foreground">{moduleName}</span> panel.
          </p>
          <div className="pt-2 flex flex-col gap-2">
            <Button onClick={() => setLocation("/dashboard")} className="w-full bg-primary text-primary-foreground font-semibold">
              Return to My Dashboard
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}

/**
 * Guard for the standalone vendor portal. Requires a signed-in Clerk user but,
 * unlike AuthGuard, does NOT call /api/me (vendors have no tenant membership).
 * The portal page itself surfaces "account not linked" if the vendor record is
 * missing.
 */
function VendorGuard({ children }: { children: React.ReactNode }) {
  const { isLoaded, userId } = useAuth();
  const [, setLocation] = useLocation();

  useEffect(() => {
    if (isLoaded && !userId) setLocation("/sign-in");
  }, [isLoaded, userId, setLocation]);

  if (!isLoaded || !userId) {
    return (
      <div className="min-h-[100dvh] flex items-center justify-center bg-[#f4f6f8]">
        <div className="flex flex-col items-center gap-3 text-slate-400">
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-slate-300 border-t-blue-600" />
          <span className="text-sm">Loading…</span>
        </div>
      </div>
    );
  }
  return <>{children}</>;
}

// Determines the correct landing page after sign-in based on the user's role.
// Super-admins land on /admin/platform; role-specific users land on their respective module dashboards.
// Rendered by the /_redirect route, which all post-login flows now point to.
function PostLoginRedirect() {
  const { isLoaded, userId } = useAuth();
  const { data: me, isError } = useQuery<Record<string, unknown>>({
    queryKey: ["me"],
    queryFn: () => customFetch<Record<string, unknown>>("/api/me"),
    enabled: isLoaded && !!userId,
    staleTime: 30_000,
    retry: 1,
  });
  const [, setLocation] = useLocation();

  useEffect(() => {
    if (!isLoaded || !userId) return;
    // If /api/me fails (vendor account, provisioning error, network, etc.),
    // fall back to /employee/dashboard — its AuthGuard re-runs /api/me and handles the
    // vendor / onboarding / error states consistently. This avoids an
    // indefinite spinner here.
    if (isError) {
      setLocation("/employee/dashboard");
      return;
    }
    if (!me) return;
    const targetRoute = getDashboardRouteForRole(me.roleName as string | undefined, me.isSuperAdmin === true);
    setLocation(targetRoute);
  }, [me, isError, isLoaded, userId, setLocation]);

  return (
    <div className="min-h-[100dvh] flex items-center justify-center bg-[#f4f6f8]">
      <div className="flex flex-col items-center gap-3 text-slate-400">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-slate-300 border-t-blue-600" />
        <span className="text-sm">Loading…</span>
      </div>
    </div>
  );
}

// Centered loading indicator shown on the auth screens while Clerk's widget is
// still loading, or while a freshly signed-in user is being redirected. Without
// this gate the static page chrome (e.g. the "Forgot your password?" footer)
// flashes on screen for a split second before/around the Clerk widget.
function AuthFallback() {
  return (
    <div className="flex flex-col items-center gap-3 text-slate-400">
      <div className="h-8 w-8 animate-spin rounded-full border-2 border-slate-300 border-t-blue-600" />
      <span className="text-sm">Loading…</span>
    </div>
  );
}

// Renders the auth UI only while the user is signed out. Once a user signs in
// (e.g. right after submitting the form, before Clerk completes the redirect to
// the dashboard) it shows the spinner instead, so the static "Forgot your
// password?" chrome never lingers on screen during the transition.
function SignedOutOnly({ children }: { children: React.ReactNode }) {
  const { isSignedIn } = useAuth();
  const [, setLocation] = useLocation();

  // If a user is already signed in (or just signed in), send them to the
  // post-login redirect handler which decides the correct landing page based
  // on their role.
  useEffect(() => {
    if (isSignedIn) setLocation("/_redirect");
  }, [isSignedIn, setLocation]);

  if (isSignedIn) return <AuthFallback />;
  return <>{children}</>;
}

// ── Demo Login Bypass ─────────────────────────────────────────────────────────
// Shows one-click persona buttons below the Clerk sign-in card.
// Uses Clerk's sign-in token strategy so no OTP / email verification is needed.
const DEMO_PERSONAS = [
  { email: "rajesh.sharma@aleatech.in",  name: "Rajesh Sharma",  role: "Admin / CEO",      icon: "👑" },
  { email: "priya.nair@aleatech.in",     name: "Priya Nair",     role: "HR Head",           icon: "👥" },
  { email: "amit.agarwal@aleatech.in",   name: "Amit Agarwal",   role: "Finance Head",      icon: "💰" },
  { email: "vikram.reddy@aleatech.in",   name: "Vikram Reddy",   role: "Sales Head",        icon: "📈" },
  { email: "neha.gupta@aleatech.in",     name: "Neha Gupta",     role: "IT Manager",        icon: "💻" },
  { email: "anjali.verma@aleatech.in",   name: "Anjali Verma",   role: "Employee Portal",   icon: "🪪" },
];

function DemoLoginPanel() {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { demoLogin } = useAuth();

  async function handleDemoLogin(email: string) {
    console.log("[DemoLogin] clicked:", email);
    setLoading(email);
    setError(null);
    try {
      const user = await demoLogin(email);
      const targetRoute = getDashboardRouteForRole(user?.roleName || user?.role, user?.isSuperAdmin);
      window.location.href = `${basePath}${targetRoute}`;
    } catch (e: unknown) {
      console.error("[DemoLogin] error:", e);
      setError(e instanceof Error ? e.message : "Demo login failed — please try again");
    } finally {
      setLoading(null);
    }
  }

  return (
    <div className="w-full mt-4 pb-10">
      <div className="bg-[#0f3455] rounded-2xl border border-white/10 shadow-lg overflow-hidden">
        {/* Collapsible header — acts as the toggle */}
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="w-full flex items-center gap-2.5 px-4 py-3 hover:bg-white/5 transition-colors"
        >
          <span className="text-xs font-semibold text-white/60 uppercase tracking-wide flex-1 text-left">
            Try a demo account
          </span>
          <svg
            className={`h-4 w-4 text-white/50 shrink-0 transition-transform duration-200 ${open ? "rotate-180" : ""}`}
            fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        {/* Expandable persona grid */}
        {open && (
          <>
            <div className="border-t border-white/10 grid grid-cols-2 gap-1.5 p-2.5">
              {DEMO_PERSONAS.map((p) => (
                <button
                  key={p.email}
                  onClick={() => handleDemoLogin(p.email)}
                  disabled={loading !== null}
                  className="flex items-center gap-2.5 rounded-xl border border-white/10 bg-white/5 px-3 py-2.5 text-left hover:bg-white/10 hover:border-blue-400/30 transition-colors disabled:opacity-50 disabled:cursor-not-allowed group"
                >
                  <span className="text-lg w-5 text-center shrink-0 leading-none">{p.icon}</span>
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-semibold text-white leading-tight truncate">{p.name}</p>
                    <p className="text-[10px] text-white/50 truncate mt-0.5">{p.role}</p>
                  </div>
                  {loading === p.email ? (
                    <div className="h-3.5 w-3.5 shrink-0 animate-spin rounded-full border-2 border-white/20 border-t-blue-300" />
                  ) : (
                    <svg className="h-3.5 w-3.5 text-white/30 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                    </svg>
                  )}
                </button>
              ))}
            </div>
            {error && (
              <div className="px-4 py-2 bg-destructive/10 border-t border-destructive/20 text-xs text-destructive font-medium">{error}</div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function Router() {
  return (
    <Switch>
      <Route path="/" component={Home} />
      <Route path="/about" component={About} />
      <Route path="/features" component={Features} />
      <Route path="/pricing" component={Pricing} />
      <Route path="/demo-access" component={DemoAccess} />
      <Route path="/contact" component={Contact} />
      <Route path="/privacy" component={Privacy} />
      <Route path="/terms" component={Terms} />
      <Route path="/f/:identifier">
        <Suspense fallback={<PageLoading />}>
          <PublicFormViewer />
        </Suspense>
      </Route>
      <Route path="/sign-in/*?">
        <Suspense fallback={<PageLoading />}>
          <SignInPage />
        </Suspense>
      </Route>
      <Route path="/signin/*?">
        <Suspense fallback={<PageLoading />}>
          <SignInPage />
        </Suspense>
      </Route>
      <Route path="/login/*?">
        <Suspense fallback={<PageLoading />}>
          <SignInPage />
        </Suspense>
      </Route>
      <Route path="/admin/login/*?">
        <Suspense fallback={<PageLoading />}>
          <SignInPage />
        </Suspense>
      </Route>
      <Route path="/admin-login/*?">
        <Suspense fallback={<PageLoading />}>
          <SignInPage />
        </Suspense>
      </Route>
      <Route path="/forgot-password/*?">
        <Suspense fallback={<PageLoading />}>
          <ForgotPasswordPage />
        </Suspense>
      </Route>
      <Route path="/forgot-password">
        <Suspense fallback={<PageLoading />}>
          <ForgotPasswordPage />
        </Suspense>
      </Route>
      <Route path="/reset-password/*?">
        <Suspense fallback={<PageLoading />}>
          <ResetPasswordPage />
        </Suspense>
      </Route>
      <Route path="/reset-password">
        <Suspense fallback={<PageLoading />}>
          <ResetPasswordPage />
        </Suspense>
      </Route>
      <Route path="/set-password/*?">
        <Suspense fallback={<PageLoading />}>
          <SetPasswordPage />
        </Suspense>
      </Route>
      <Route path="/set-password">
        <Suspense fallback={<PageLoading />}>
          <SetPasswordPage />
        </Suspense>
      </Route>
      <Route path="/auth/set-password/*?">
        <Suspense fallback={<PageLoading />}>
          <SetPasswordPage />
        </Suspense>
      </Route>
      <Route path="/auth/set-password">
        <Suspense fallback={<PageLoading />}>
          <SetPasswordPage />
        </Suspense>
      </Route>
      <Route path="/onboarding/set-password/*?">
        <Suspense fallback={<PageLoading />}>
          <SetPasswordPage />
        </Suspense>
      </Route>
      <Route path="/onboarding/set-password">
        <Suspense fallback={<PageLoading />}>
          <SetPasswordPage />
        </Suspense>
      </Route>
      <Route path="/activate/*?">
        <Suspense fallback={<PageLoading />}>
          <SetPasswordPage />
        </Suspense>
      </Route>
      <Route path="/activate">
        <Suspense fallback={<PageLoading />}>
          <SetPasswordPage />
        </Suspense>
      </Route>
      <Route path="/auth/activate/*?">
        <Suspense fallback={<PageLoading />}>
          <SetPasswordPage />
        </Suspense>
      </Route>
      <Route path="/auth/activate">
        <Suspense fallback={<PageLoading />}>
          <SetPasswordPage />
        </Suspense>
      </Route>
      <Route path="/sign-up/*?">
        <Suspense fallback={<PageLoading />}>
          <SignUpPage />
        </Suspense>
      </Route>
      <Route path="/signup/*?">
        <Suspense fallback={<PageLoading />}>
          <SignUpPage />
        </Suspense>
      </Route>
      <Route path="/register/*?">
        <Suspense fallback={<PageLoading />}>
          <SignUpPage />
        </Suspense>
      </Route>
      <Route path="/tenant/register/*?">
        <Suspense fallback={<PageLoading />}>
          <SignUpPage />
        </Suspense>
      </Route>
      <Route path="/create-company/*?">
        <Suspense fallback={<PageLoading />}>
          <SignUpPage />
        </Suspense>
      </Route>

      <Route path="/onboarding">
        <AuthGuard skipOnboardingCheck>
          <Suspense fallback={<PageLoading />}>
            <OnboardingPage />
          </Suspense>
        </AuthGuard>
      </Route>

      {/* Smart post-login redirect: super-admins → /admin/platform, everyone else → /dashboard */}
      <Route path="/_redirect">
        <AuthGuard>
          <PostLoginRedirect />
        </AuthGuard>
      </Route>

      <Route path="/dashboard">
        <AuthGuard>
          <RequireModuleAccess moduleName="Home Dashboard">
            <AppShell breadcrumbs={[{ label: "Home" }]}>
              <Suspense fallback={<PageLoading />}>
                <Launchpad />
              </Suspense>
            </AppShell>
          </RequireModuleAccess>
        </AuthGuard>
      </Route>

      <Route path="/organization">
        <AuthGuard>
          <RequireModuleAccess moduleName="Organization Settings">
            <AppShell breadcrumbs={[{ label: "Organization" }]}>
              <Suspense fallback={<PageLoading />}>
                <OrganizationPage />
              </Suspense>
            </AppShell>
          </RequireModuleAccess>
        </AuthGuard>
      </Route>

      <Route path="/users">
        <AuthGuard>
          <RequireModuleAccess moduleName="Users & Roles">
            <AppShell breadcrumbs={[{ label: "Users & Roles" }]}>
              <Suspense fallback={<PageLoading />}>
                <UsersPage />
              </Suspense>
            </AppShell>
          </RequireModuleAccess>
        </AuthGuard>
      </Route>

      <Route path="/audit-log">
        <AuthGuard>
          <RequireModuleAccess moduleName="Audit Log">
            <AppShell breadcrumbs={[{ label: "Audit Log" }]}>
              <Suspense fallback={<PageLoading />}>
                <AuditLogPage />
              </Suspense>
            </AppShell>
          </RequireModuleAccess>
        </AuthGuard>
      </Route>

      <Route path="/hr/employee/:id">
        <AuthGuard>
          <RequireModuleAccess moduleName="Human Resources">
            <AppShell breadcrumbs={[{ label: "Human Resources", href: "/hr" }, { label: "Employee Profile" }]}>
              <Suspense fallback={<PageLoading />}>
                <EmployeeProfilePage />
              </Suspense>
            </AppShell>
          </RequireModuleAccess>
        </AuthGuard>
      </Route>

      <Route path="/hr">
        <AuthGuard>
          <RequireModuleAccess moduleName="Human Resources">
            <AppShell breadcrumbs={[{ label: "Human Resources" }]}>
              <Suspense fallback={<PageLoading />}>
                <HRPage />
              </Suspense>
            </AppShell>
          </RequireModuleAccess>
        </AuthGuard>
      </Route>

      <Route path="/hr/dashboard/*?">
        <AuthGuard>
          <RequireModuleAccess moduleName="Human Resources">
            <AppShell breadcrumbs={[{ label: "Human Resources" }]}>
              <Suspense fallback={<PageLoading />}>
                <HRPage />
              </Suspense>
            </AppShell>
          </RequireModuleAccess>
        </AuthGuard>
      </Route>

      <Route path="/employee/*?">
        <AuthGuard>
          <RequireModuleAccess moduleName="Employee Self-Service">
            <AppShell breadcrumbs={[{ label: "My Portal" }]}>
              <Suspense fallback={<PageLoading />}>
                <EmployeePage />
              </Suspense>
            </AppShell>
          </RequireModuleAccess>
        </AuthGuard>
      </Route>

      <Route path="/my-portal/*?">
        <AuthGuard>
          <RequireModuleAccess moduleName="Employee Self-Service">
            <AppShell breadcrumbs={[{ label: "My Portal" }]}>
              <Suspense fallback={<PageLoading />}>
                <EmployeePage />
              </Suspense>
            </AppShell>
          </RequireModuleAccess>
        </AuthGuard>
      </Route>

      <Route path="/settings/*?">
        <AuthGuard>
          <RequireModuleAccess moduleName="Employee Self-Service">
            <AppShell breadcrumbs={[{ label: "Account Settings" }]}>
              <Suspense fallback={<PageLoading />}>
                <AccountSettingsPage />
              </Suspense>
            </AppShell>
          </RequireModuleAccess>
        </AuthGuard>
      </Route>

      <Route path="/account-settings/*?">
        <AuthGuard>
          <RequireModuleAccess moduleName="Employee Self-Service">
            <AppShell breadcrumbs={[{ label: "Account Settings" }]}>
              <Suspense fallback={<PageLoading />}>
                <AccountSettingsPage />
              </Suspense>
            </AppShell>
          </RequireModuleAccess>
        </AuthGuard>
      </Route>

      <Route path="/operations/account-settings/*?">
        <AuthGuard>
          <RequireModuleAccess moduleName="Employee Self-Service">
            <AppShell breadcrumbs={[{ label: "Operations", href: "/operations" }, { label: "Account Settings" }]}>
              <Suspense fallback={<PageLoading />}>
                <AccountSettingsPage />
              </Suspense>
            </AppShell>
          </RequireModuleAccess>
        </AuthGuard>
      </Route>

      <Route path="/operations/settings/*?">
        <AuthGuard>
          <RequireModuleAccess moduleName="Employee Self-Service">
            <AppShell breadcrumbs={[{ label: "Operations", href: "/operations" }, { label: "Account Settings" }]}>
              <Suspense fallback={<PageLoading />}>
                <AccountSettingsPage />
              </Suspense>
            </AppShell>
          </RequireModuleAccess>
        </AuthGuard>
      </Route>

      <Route path="/profile/*?">
        <AuthGuard>
          <RequireModuleAccess moduleName="Employee Self-Service">
            <AppShell breadcrumbs={[{ label: "Account Settings" }]}>
              <Suspense fallback={<PageLoading />}>
                <AccountSettingsPage />
              </Suspense>
            </AppShell>
          </RequireModuleAccess>
        </AuthGuard>
      </Route>

      <Route path="/finance">
        <AuthGuard>
          <RequireModuleAccess moduleName="Finance & Accounts">
            <AppShell breadcrumbs={[{ label: "Finance & Accounts" }]}>
              <Suspense fallback={<PageLoading />}>
                <FinancePage />
              </Suspense>
            </AppShell>
          </RequireModuleAccess>
        </AuthGuard>
      </Route>

      <Route path="/finance/dashboard/*?">
        <AuthGuard>
          <RequireModuleAccess moduleName="Finance & Accounts">
            <AppShell breadcrumbs={[{ label: "Finance & Accounts" }]}>
              <Suspense fallback={<PageLoading />}>
                <FinancePage />
              </Suspense>
            </AppShell>
          </RequireModuleAccess>
        </AuthGuard>
      </Route>

      <Route path="/vendor-portal">
        <VendorGuard>
          <VendorShell>
            <Suspense fallback={<PageLoading />}>
              <VendorPortalPage />
            </Suspense>
          </VendorShell>
        </VendorGuard>
      </Route>

      <Route path="/catalog">
        <AuthGuard>
          <RequireModuleAccess moduleName="Products & Catalog">
            <AppShell breadcrumbs={[{ label: "Products & Catalog" }]}>
              <Suspense fallback={<PageLoading />}>
                <CatalogPage />
              </Suspense>
            </AppShell>
          </RequireModuleAccess>
        </AuthGuard>
      </Route>

      <Route path="/payroll/*?">
        <AuthGuard>
          <RequireModuleAccess moduleName="Payroll">
            <AppShell breadcrumbs={[{ label: "Finance & Accounts", href: "/finance" }, { label: "Payroll" }]}>
              <Suspense fallback={<PageLoading />}>
                <PayrollPage />
              </Suspense>
            </AppShell>
          </RequireModuleAccess>
        </AuthGuard>
      </Route>

      <Route path="/finance/payroll/*?">
        <AuthGuard>
          <RequireModuleAccess moduleName="Payroll">
            <AppShell breadcrumbs={[{ label: "Finance & Accounts", href: "/finance" }, { label: "Payroll" }]}>
              <Suspense fallback={<PageLoading />}>
                <PayrollPage />
              </Suspense>
            </AppShell>
          </RequireModuleAccess>
        </AuthGuard>
      </Route>

      <Route path="/operations/finance/payroll/*?">
        <AuthGuard>
          <RequireModuleAccess moduleName="Payroll">
            <AppShell breadcrumbs={[{ label: "Finance & Accounts", href: "/finance" }, { label: "Payroll" }]}>
              <Suspense fallback={<PageLoading />}>
                <PayrollPage />
              </Suspense>
            </AppShell>
          </RequireModuleAccess>
        </AuthGuard>
      </Route>

      <Route path="/operations/payroll/*?">
        <AuthGuard>
          <RequireModuleAccess moduleName="Payroll">
            <AppShell breadcrumbs={[{ label: "Operations", href: "/operations" }, { label: "Payroll" }]}>
              <Suspense fallback={<PageLoading />}>
                <PayrollPage />
              </Suspense>
            </AppShell>
          </RequireModuleAccess>
        </AuthGuard>
      </Route>

      <Route path="/tally">
        <AuthGuard>
          <RequireModuleAccess moduleName="Tally Integration">
            <AppShell breadcrumbs={[{ label: "Tally Integration" }]}>
              <Suspense fallback={<PageLoading />}>
                <TallyPage />
              </Suspense>
            </AppShell>
          </RequireModuleAccess>
        </AuthGuard>
      </Route>

      <Route path="/inventory">
        <AuthGuard>
          <RequireModuleAccess moduleName="Stock & Inventory">
            <AppShell breadcrumbs={[{ label: "Stock & Inventory" }]}>
              <Suspense fallback={<PageLoading />}>
                <InventoryPage />
              </Suspense>
            </AppShell>
          </RequireModuleAccess>
        </AuthGuard>
      </Route>

      <Route path="/procurement">
        <AuthGuard>
          <RequireModuleAccess moduleName="Procurement">
            <AppShell breadcrumbs={[{ label: "Procurement" }]}>
              <Suspense fallback={<PageLoading />}>
                <ProcurementPage />
              </Suspense>
            </AppShell>
          </RequireModuleAccess>
        </AuthGuard>
      </Route>

      <Route path="/crm">
        <AuthGuard>
          <RequireModuleAccess moduleName="CRM & Sales">
            <AppShell breadcrumbs={[{ label: "CRM & Sales" }]}>
              <Suspense fallback={<PageLoading />}>
                <CrmPage />
              </Suspense>
            </AppShell>
          </RequireModuleAccess>
        </AuthGuard>
      </Route>

      <Route path="/operations">
        <AuthGuard>
          <RequireModuleAccess moduleName="Operations">
            <AppShell breadcrumbs={[{ label: "Operations" }]}>
              <Suspense fallback={<PageLoading />}>
                <OperationsPage />
              </Suspense>
            </AppShell>
          </RequireModuleAccess>
        </AuthGuard>
      </Route>

      <Route path="/operations/">
        <AuthGuard>
          <RequireModuleAccess moduleName="Operations">
            <AppShell breadcrumbs={[{ label: "Operations" }]}>
              <Suspense fallback={<PageLoading />}>
                <OperationsPage />
              </Suspense>
            </AppShell>
          </RequireModuleAccess>
        </AuthGuard>
      </Route>

      <Route path="/admin">
        <AuthGuard>
          <RequireModuleAccess moduleName="Admin & IT">
            <AppShell breadcrumbs={[{ label: "Admin & IT" }]}>
              <Suspense fallback={<PageLoading />}>
                <AdminPage />
              </Suspense>
            </AppShell>
          </RequireModuleAccess>
        </AuthGuard>
      </Route>

      <Route path="/admin/dashboard/*?">
        <AuthGuard>
          <RequireModuleAccess moduleName="Admin & IT">
            <AppShell breadcrumbs={[{ label: "Admin & IT" }]}>
              <Suspense fallback={<PageLoading />}>
                <AdminPage />
              </Suspense>
            </AppShell>
          </RequireModuleAccess>
        </AuthGuard>
      </Route>

      <Route path="/it">
        <AuthGuard>
          <RequireModuleAccess moduleName="Admin & IT">
            <AppShell breadcrumbs={[{ label: "Admin & IT" }]}>
              <Suspense fallback={<PageLoading />}>
                <AdminPage />
              </Suspense>
            </AppShell>
          </RequireModuleAccess>
        </AuthGuard>
      </Route>

      <Route path="/it/dashboard/*?">
        <AuthGuard>
          <RequireModuleAccess moduleName="Admin & IT">
            <AppShell breadcrumbs={[{ label: "Admin & IT" }]}>
              <Suspense fallback={<PageLoading />}>
                <AdminPage />
              </Suspense>
            </AppShell>
          </RequireModuleAccess>
        </AuthGuard>
      </Route>

      {/* Platform Admin is a standalone surface with its own chrome — it must
          NOT be wrapped in the tenant AppShell (company switcher, rail, etc.). */}
      <Route path="/admin/platform">
        <SuperAdminGuard>
          <Suspense fallback={<PageLoading />}>
            <PlatformAdminHub />
          </Suspense>
        </SuperAdminGuard>
      </Route>

      <Route path="/admin/platform/:section">
        <SuperAdminGuard>
          <Suspense fallback={<PageLoading />}>
            <PlatformAdminHub />
          </Suspense>
        </SuperAdminGuard>
      </Route>

      <Route path="/reports">
        <AuthGuard>
          <RequireModuleAccess moduleName="MIS & Reports">
            <AppShell breadcrumbs={[{ label: "MIS & Reports" }]}>
              <Suspense fallback={<PageLoading />}>
                <ReportsPage />
              </Suspense>
            </AppShell>
          </RequireModuleAccess>
        </AuthGuard>
      </Route>

      <Route path="/recruitment">
        <AuthGuard>
          <RequireModuleAccess moduleName="Recruitment & ATS">
            <AppShell breadcrumbs={[{ label: "Recruitment & ATS" }]}>
              <Suspense fallback={<PageLoading />}>
                <RecruitmentPage />
              </Suspense>
            </AppShell>
          </RequireModuleAccess>
        </AuthGuard>
      </Route>

      <Route path="/quality">
        <AuthGuard>
          <RequireModuleAccess moduleName="Quality Management">
            <AppShell breadcrumbs={[{ label: "Quality Management" }]}>
              <Suspense fallback={<PageLoading />}>
                <QualityPage />
              </Suspense>
            </AppShell>
          </RequireModuleAccess>
        </AuthGuard>
      </Route>

      <Route path="/manufacturing">
        <AuthGuard>
          <RequireModuleAccess moduleName="Manufacturing">
            <AppShell breadcrumbs={[{ label: "Manufacturing" }]}>
              <Suspense fallback={<PageLoading />}>
                <ManufacturingPage />
              </Suspense>
            </AppShell>
          </RequireModuleAccess>
        </AuthGuard>
      </Route>

      <Route path="/approvals">
        <AuthGuard>
          <RequireModuleAccess moduleName="Approvals">
            <AppShell breadcrumbs={[{ label: "Approvals" }]}>
              <Suspense fallback={<PageLoading />}>
                <ApprovalsPage />
              </Suspense>
            </AppShell>
          </RequireModuleAccess>
        </AuthGuard>
      </Route>

      <Route path="/performance">
        <AuthGuard>
          <RequireModuleAccess moduleName="Performance Management">
            <AppShell breadcrumbs={[{ label: "Performance" }]}>
              <Suspense fallback={<PageLoading />}>
                <PerformancePage />
              </Suspense>
            </AppShell>
          </RequireModuleAccess>
        </AuthGuard>
      </Route>

      <Route path="/service">
        <AuthGuard>
          <RequireModuleAccess moduleName="Customer & Field Service">
            <AppShell breadcrumbs={[{ label: "Customer & Field Service" }]}>
              <Suspense fallback={<PageLoading />}>
                <ServicePage />
              </Suspense>
            </AppShell>
          </RequireModuleAccess>
        </AuthGuard>
      </Route>

      <Route path="/integrations">
        <AuthGuard>
          <RequireModuleAccess moduleName="Integrations">
            <AppShell breadcrumbs={[{ label: "Integrations" }]}>
              <Suspense fallback={<PageLoading />}>
                <IntegrationsPage />
              </Suspense>
            </AppShell>
          </RequireModuleAccess>
        </AuthGuard>
      </Route>

      <Route path="/dms">
        <AuthGuard>
          <RequireModuleAccess moduleName="Document Management">
            <AppShell breadcrumbs={[{ label: "Document Management" }]}>
              <Suspense fallback={<PageLoading />}>
                <DmsPage />
              </Suspense>
            </AppShell>
          </RequireModuleAccess>
        </AuthGuard>
      </Route>

      <Route path="/documents">
        <AuthGuard>
          <RequireModuleAccess moduleName="Document Management">
            <AppShell breadcrumbs={[{ label: "Document Management" }]}>
              <Suspense fallback={<PageLoading />}>
                <DmsPage />
              </Suspense>
            </AppShell>
          </RequireModuleAccess>
        </AuthGuard>
      </Route>

      <Route path="/operations/documents">
        <AuthGuard>
          <RequireModuleAccess moduleName="Document Management">
            <AppShell breadcrumbs={[{ label: "Operations", href: "/operations" }, { label: "Documents" }]}>
              <Suspense fallback={<PageLoading />}>
                <DmsPage />
              </Suspense>
            </AppShell>
          </RequireModuleAccess>
        </AuthGuard>
      </Route>

      <Route path="/ld">
        <AuthGuard>
          <RequireModuleAccess moduleName="Learning & Development">
            <AppShell breadcrumbs={[{ label: "Learning & Development" }]}>
              <Suspense fallback={<PageLoading />}>
                <LDPage />
              </Suspense>
            </AppShell>
          </RequireModuleAccess>
        </AuthGuard>
      </Route>

      <Route path="/travel">
        <AuthGuard>
          <RequireModuleAccess moduleName="Travel & Expense">
            <AppShell breadcrumbs={[{ label: "Travel & Expense" }]}>
              <Suspense fallback={<PageLoading />}>
                <TravelPage />
              </Suspense>
            </AppShell>
          </RequireModuleAccess>
        </AuthGuard>
      </Route>

      <Route path="/helpdesk">
        <AuthGuard>
          <RequireModuleAccess moduleName="Help Desk">
            <AppShell breadcrumbs={[{ label: "Help Desk" }]}>
              <Suspense fallback={<PageLoading />}>
                <HelpdeskPage />
              </Suspense>
            </AppShell>
          </RequireModuleAccess>
        </AuthGuard>
      </Route>

      <Route path="/dev-tools">
        <AuthGuard>
          <AppShell breadcrumbs={[{ label: "Developer Tools" }]}>
            <Suspense fallback={<PageLoading />}>
              <DevToolsPage />
            </Suspense>
          </AppShell>
        </AuthGuard>
      </Route>

      <Route path="/admin/packages">
        <SuperAdminGuard>
          <AppShell breadcrumbs={[{ label: "Super Admin" }, { label: "SaaS Subscription Packages" }]}>
            <Suspense fallback={<PageLoading />}>
              <SuperAdminPackagesPage />
            </Suspense>
          </AppShell>
        </SuperAdminGuard>
      </Route>

      <Route path="/pricing/details" component={PackageDetails} />
      <Route path="/pricing" component={Pricing} />

      <Route component={NotFound} />
    </Switch>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <QueryClientProvider client={queryClient}>
          <ClerkTokenSync />
          <ThemeDbSync />
          <ClerkQueryClientCacheInvalidator />
          <TooltipProvider>
            <WouterRouter base={basePath}>
              <Router />
            </WouterRouter>
            <Toaster />
          </TooltipProvider>
        </QueryClientProvider>
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
